import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import classroomTaskService from '../../../services/classroomTaskService';
import documentService from '../../../services/documentService';
import estudiantesService from '../../../services/estudiantesService';
import types from '../../../services/types';
import utils from '../../../utils/generalFunctions';
import Loading from '../../../components/Loading';
import HtmlEditor from '../../../components/HtmlEditor';
import sanitizeHtml from '../../../utils/sanitizeHtml';
import '../AulaVirtual.css';

const TASK_TYPE_LABEL = {
    [types.classroomTaskTypes.TAREA]: 'Tarea',
    [types.classroomTaskTypes.ACTIVIDAD]: 'Actividad',
    [types.classroomTaskTypes.TALLER]: 'Taller'
};

const TaskManage = () => {
    const { taskId } = useParams();
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const esDocente = Number(utils.getUserInformation()?.userTypeId) === types.userTypes.TEACHER;

    const load = async () => {
        try {
            const t = await classroomTaskService.getTask(taskId);
            setTask(t);
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, [taskId]);

    if (loading) return <Loading />;
    if (!task || !task.taskId) return <div className="alert alert-warning av-module">No encontramos esta tarea.</div>;

    return (
        <div className="container-fluid p-0 av-module">
            <TaskHeader task={task} onSaved={load} esDocente={esDocente} />
            {esDocente ? <TaskRoster task={task} /> : <TaskSubmit task={task} />}
        </div>
    );
};

const TaskHeader = ({ task, onSaved, esDocente }) => {
    const [editando, setEditando] = useState(false);
    const [formData, setFormData] = useState(task);

    const guardar = async () => {
        const user = utils.getUserInformation();
        try {
            await classroomTaskService.updateTask({ ...formData, modificationUser: user?.userId });
            Swal.fire('Listo', 'Tarea actualizada.', 'success');
            setEditando(false);
            onSaved();
        } catch (error) {
            Swal.fire('Ha ocurrido un error', 'Por favor intentelo más tarde', 'error');
        }
    };

    return (
        <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div>
                    <span className="badge bg-info me-2">{TASK_TYPE_LABEL[task.taskTypeId]}</span>
                    <strong>{task.title}</strong>
                </div>
                {esDocente && (
                    <button className="btn btn-sm btn-outline-info" onClick={() => setEditando(!editando)}>
                        <i className="fas fa-pencil-alt me-1"></i> {editando ? 'Cancelar' : 'Editar'}
                    </button>
                )}
            </div>
            <div className="card-body">
                {!editando ? (
                    <>
                        {task.instructionsHtml && <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(task.instructionsHtml) }} />}
                        <div className="row mt-3 text-muted small">
                            {task.openDate && <div className="col-auto">Apertura: {new Date(task.openDate).toLocaleString()}</div>}
                            {task.dueDate && <div className="col-auto">Entrega: {new Date(task.dueDate).toLocaleString()}</div>}
                            <div className="col-auto">Nota máxima: {task.maxScore}</div>
                            <div className="col-auto">{task.allowLateSubmission ? 'Acepta entregas tardías' : 'No acepta entregas tardías'}</div>
                        </div>
                    </>
                ) : (
                    <div>
                        <div className="mb-3">
                            <label className="form-label">Título</label>
                            <input type="text" className="form-control" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Instrucciones</label>
                            <HtmlEditor value={formData.instructionsHtml || ''} onChange={(v) => setFormData({ ...formData, instructionsHtml: v })} />
                        </div>
                        <div className="row">
                            <div className="col-md-4 mb-3">
                                <label className="form-label">Fecha de entrega</label>
                                <input type="datetime-local" className="form-control" value={formData.dueDate ? formData.dueDate.substring(0, 16) : ''} onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })} />
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label">Nota máxima</label>
                                <input type="number" step="0.01" className="form-control" value={formData.maxScore} onChange={(e) => setFormData({ ...formData, maxScore: e.target.value })} />
                            </div>
                            <div className="col-md-4 mb-3 d-flex align-items-end">
                                <div className="form-check">
                                    <input type="checkbox" className="form-check-input" id="editAllowLate" checked={formData.allowLateSubmission} onChange={(e) => setFormData({ ...formData, allowLateSubmission: e.target.checked })} />
                                    <label className="form-check-label" htmlFor="editAllowLate">Acepta tardías</label>
                                </div>
                            </div>
                        </div>
                        <button className="btn btn-primary" onClick={guardar}>Guardar cambios</button>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Vista docente: roster de calificación (requisito 5) ---
const TaskRoster = ({ task }) => {
    const [roster, setRoster] = useState([]);
    const [loading, setLoading] = useState(true);
    const [calificando, setCalificando] = useState(null);

    const load = async () => {
        try {
            const data = await classroomTaskService.getRoster(task.taskId);
            setRoster(data || []);
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, [task.taskId]);

    if (loading) return <Loading />;

    return (
        <div className="card">
            <div className="card-header"><strong>Estudiantes ({roster.length})</strong></div>
            <div className="table-responsive">
                <table className="table mb-0">
                    <thead>
                        <tr>
                            <th>Estudiante</th>
                            <th>Estado de entrega</th>
                            <th>Fecha</th>
                            <th>Nota</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {roster.map((r) => (
                            <tr key={r.studentId}>
                                <td>{r.studentName}</td>
                                <td>
                                    {r.submissionStatusName
                                        ? <span className="badge bg-secondary">{r.submissionStatusName}</span>
                                        : <span className="badge bg-light text-dark">Pendiente</span>}
                                </td>
                                <td>{r.submittedDate ? new Date(r.submittedDate).toLocaleString() : '-'}</td>
                                <td>{r.score ?? '-'} / {task.maxScore}</td>
                                <td className="btn-group btn-group-sm">
                                    <button className="btn btn-outline-primary" onClick={() => setCalificando(r)}>Calificar</button>
                                    {r.submissionId && (
                                        <button
                                            className="btn btn-outline-warning"
                                            onClick={async () => {
                                                await classroomTaskService.returnSubmission(r.submissionId, utils.getUserInformation()?.userId);
                                                load();
                                            }}
                                        >
                                            Devolver
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {calificando && (
                <GradeModal
                    task={task}
                    roster={calificando}
                    onClose={() => { setCalificando(null); load(); }}
                />
            )}
        </div>
    );
};

const GradeModal = ({ task, roster, onClose }) => {
    const [submission, setSubmission] = useState(null);
    const [score, setScore] = useState(roster.score ?? '');
    const [feedbackHtml, setFeedbackHtml] = useState(roster.feedbackHtml ?? '');
    const [loading, setLoading] = useState(!!roster.submissionId);

    useEffect(() => {
        const cargar = async () => {
            if (roster.submissionId) {
                const sub = await classroomTaskService.getSubmission(roster.submissionId);
                setSubmission(sub);
            }
            setLoading(false);
        };
        cargar();
    }, [roster.submissionId]);

    const guardar = async () => {
        const user = utils.getUserInformation();
        try {
            await classroomTaskService.saveGrade({
                taskId: task.taskId,
                studentId: roster.studentId,
                submissionId: roster.submissionId || null,
                score: score === '' ? null : Number(score),
                feedbackHtml,
                createdUser: user?.userId,
                modificationUser: user?.userId
            });
            Swal.fire('Listo', 'Nota guardada.', 'success');
            onClose();
        } catch (error) {
            Swal.fire('Ha ocurrido un error', 'Por favor intentelo más tarde', 'error');
        }
    };

    return (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Calificar a {roster.studentName}</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {loading ? <Loading /> : (
                            <>
                                {submission ? (
                                    <div className="mb-3">
                                        <strong>Entrega:</strong>
                                        <p>{submission.submissionText || <em>Sin texto</em>}</p>
                                        {submission.documents?.length > 0 && (
                                            <ul>
                                                {submission.documents.map((d) => (
                                                    <li key={d.documentId}><a href={d.url} target="_blank" rel="noreferrer">{d.name}</a></li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ) : (
                                    <div className="alert alert-secondary">Este estudiante todavía no ha entregado.</div>
                                )}
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Nota (máx {task.maxScore})</label>
                                        <input type="number" step="0.01" className="form-control" value={score} onChange={(e) => setScore(e.target.value)} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Retroalimentación</label>
                                    <HtmlEditor value={feedbackHtml} onChange={setFeedbackHtml} />
                                </div>
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-primary" onClick={guardar}>Guardar nota</button>
                        <button className="btn btn-outline-secondary" onClick={onClose}>Cerrar</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Vista estudiante: entregar + ver nota ---
const TaskSubmit = ({ task }) => {
    const [studentId, setStudentId] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [grade, setGrade] = useState(null);
    const [texto, setTexto] = useState('');
    const [archivos, setArchivos] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const estudiante = await estudiantesService.getEstudiantesPorUsuario();
            if (!estudiante || !estudiante.studentId) { setLoading(false); return; }
            setStudentId(estudiante.studentId);
            const subs = await classroomTaskService.getSubmissionsForStudent(task.taskId, estudiante.studentId);
            setSubmissions(subs || []);
            const g = await classroomTaskService.getGradeForStudent(task.taskId, estudiante.studentId);
            setGrade(g);
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, [task.taskId]);

    const entregar = async () => {
        const user = utils.getUserInformation();
        try {
            const sub = await classroomTaskService.submitTask({
                taskId: task.taskId,
                studentId,
                submissionText: texto,
                createdUser: user?.userId,
                modificationUser: user?.userId
            });
            for (const archivo of archivos) {
                await documentService.addDocument(types.documentTypes.TASK_SUBMISSION_FILE, archivo, sub.submissionId, archivo.name, archivo.size, archivo.type);
            }
            Swal.fire('Listo', 'Entrega registrada con éxito.', 'success');
            setTexto('');
            setArchivos([]);
            load();
        } catch (error) {
            Swal.fire('No se pudo entregar', error?.response?.data?.message || 'Por favor intentelo más tarde', 'error');
        }
    };

    if (loading) return <Loading />;
    if (!studentId) return <div className="alert alert-warning">No encontramos tu información de estudiante.</div>;

    const ultima = submissions[submissions.length - 1];
    const puedeEntregar = !ultima || ultima.statusId === types.classroomSubmissionStatus.DEVUELTO;

    return (
        <div className="card">
            <div className="card-header"><strong>Tu entrega</strong></div>
            <div className="card-body">
                {grade && grade.score !== null && grade.score !== undefined && (
                    <div className="alert alert-success">
                        <strong>Nota: {grade.score} / {task.maxScore}</strong>
                        {grade.feedbackHtml && <div className="mt-2" dangerouslySetInnerHTML={{ __html: sanitizeHtml(grade.feedbackHtml) }} />}
                    </div>
                )}

                {submissions.length > 0 && (
                    <ul className="list-group mb-3">
                        {submissions.map((s) => (
                            <li className="list-group-item d-flex justify-content-between align-items-center" key={s.submissionId}>
                                <span>Intento {s.attemptNumber} · {new Date(s.submittedDate).toLocaleString()}</span>
                                <span className="badge bg-secondary">{s.statusName}</span>
                            </li>
                        ))}
                    </ul>
                )}

                {puedeEntregar ? (
                    <>
                        <div className="mb-3">
                            <label className="form-label">Tu respuesta</label>
                            <textarea className="form-control" rows="4" value={texto} onChange={(e) => setTexto(e.target.value)} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Archivos adjuntos</label>
                            <input type="file" className="form-control" multiple onChange={(e) => setArchivos(Array.from(e.target.files))} />
                        </div>
                        <button className="btn btn-primary" onClick={entregar}>Entregar</button>
                    </>
                ) : (
                    <div className="alert alert-info mb-0">Ya entregaste esta {TASK_TYPE_LABEL[task.taskTypeId]?.toLowerCase()}.</div>
                )}
            </div>
        </div>
    );
};

export default TaskManage;
